import React, { useCallback, useEffect, useState } from 'react'
import { DraftCommentProvider } from '@sketch/modules-common'
import { AnnotationOverlayContext } from '../../../../annotations/components'
import {
  useLockCamera,
  useGetNodeAtPosition,
  useMapRelativePositionToCameraPosition,
  usePRFileSections,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'
import { MouseCoordinates } from '../../../../annotations/types'
import { AnnotationDotFragment } from '@sketch/gql-types'
import {
  useHideAnnotationDots,
  useVisibleEntityUUIDs,
} from '../../../../annotations/hooks'
import {
  getAnnotatableNode,
  getAnnotationAnchorUUID,
  isAnnotationOnPage,
} from '../../../../annotations/utils'

interface PageAnnotationsOverlayContextProps
  extends Pick<
    React.ComponentProps<typeof AnnotationOverlayContext>,
    'disabled' | 'children' | 'hidden'
  > {
  isViewingLatestVersion: boolean
  permanentPageId: string
  /**
   * When set (single-Section focus view), only annotations belonging to this
   * Section are rendered - not every annotation on the page. Leave undefined in
   * the full page view to keep showing all of the page's annotations.
   */
  sectionUUID?: string
}

const PageAnnotationsOverlayContext = (
  props: PageAnnotationsOverlayContextProps
) => {
  const { permanentPageId, hidden, sectionUUID, ...otherProps } = props

  const fileSections = usePRFileSections()

  const visibleEntityUUIDs = useVisibleEntityUUIDs({ sectionUUID })
  const isFocusView = !!sectionUUID

  const status = useStatus()
  const lockCamera = useLockCamera()
  const [lockCameraState, setLockCameraState] = useState(false)

  useEffect(() => {
    lockCamera(lockCameraState)
  }, [lockCamera, lockCameraState])

  const getNodeAtPosition = useGetNodeAtPosition()
  const mapRelativePosition = useMapRelativePositionToCameraPosition()

  // Bounds come from the PRFile, the same source the annotation dots are
  // positioned against, so a Section anchor lands where it was placed.
  const focusedSection = (fileSections ?? []).find(
    section => section.objectId === sectionUUID
  )

  const getElementAtPosition = useCallback(
    async (mouseCoordinates: MouseCoordinates) => {
      const node = await getNodeAtPosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
      const annotatable = getAnnotatableNode(node)

      if (annotatable?.type === 'ARTBOARD') {
        return {
          type: 'ARTBOARD' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId,
        }
      }

      if (annotatable?.type === 'SECTION') {
        return {
          type: 'SECTION' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId,
        }
      }

      // In the single-Section focus view the page isn't on the canvas, so anchor
      // to the focused Section instead - a page annotation placed here would be
      // filtered straight back out of the view it was created in.
      if (focusedSection) {
        return {
          type: 'SECTION' as const,
          permanentId: focusedSection.objectId,
          bounds: focusedSection.bounds,
          permanentPageId,
        }
      }

      return {
        type: 'PAGE' as const,
        permanentId: permanentPageId,
      }
    },
    [getNodeAtPosition, permanentPageId, focusedSection]
  )

  const getPositionRelativeToOrigin = useCallback(
    (mouseCoordinates: MouseCoordinates) => {
      if (status.type !== 'READY') {
        return null
      }

      return mapRelativePosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
    },
    [mapRelativePosition, status]
  )

  const shouldRenderAnnotation = useCallback(
    (annotationDot: AnnotationDotFragment) => {
      const anchorUUID = getAnnotationAnchorUUID(annotationDot)

      // Pinned to an entity: render it when that entity is on the canvas. One
      // check covers what used to be three - a focus view shows only what the
      // focused entity holds, another page's entities are not in this page's
      // file, and an annotation whose frame/Section was deleted no longer
      // resolves to anything in the scene.
      if (visibleEntityUUIDs && anchorUUID) {
        return visibleEntityUUIDs.has(anchorUUID)
      }

      // Pinned to the page itself - or the file isn't parsed yet, in which case
      // the page is all we can match on. Either way a focus view doesn't show
      // it: the page is not what's on the canvas there.
      return !isFocusView && isAnnotationOnPage(annotationDot, permanentPageId)
    },
    [permanentPageId, isFocusView, visibleEntityUUIDs]
  )

  const [hideAnnotationsDots] = useHideAnnotationDots()
  return (
    <DraftCommentProvider>
      <AnnotationOverlayContext
        onMovingAnnotation={setLockCameraState}
        getPositionRelativeToOrigin={getPositionRelativeToOrigin}
        getElementAtPosition={getElementAtPosition}
        hidden={hideAnnotationsDots || hidden}
        shouldRenderAnnotation={shouldRenderAnnotation}
        {...otherProps}
      />
    </DraftCommentProvider>
  )
}

export default PageAnnotationsOverlayContext
